import type {CSSProperties} from 'react';
import {WEDGE_BLIP_SHEET} from '@territory/tokens';
import {radius, weight} from '../scale.js';
import {wedgeExactRole} from './wedgeRoles.js';

/* The mic comes in three paints (idle, listening, listening with a pulse) and two cursors (busy, not busy). The send button comes in two paints
   (it can send, it cannot) and two motions (a fade, none). Every name is built from these parts. */
export type WedgeBlipSheetLook =
  | 'blip-head-icon' | 'blip-head-new' | 'blip-head-new-busy' | 'blip-ctx-clear' | 'blip-hands-free' | 'blip-hands-free-on'
  | 'blip-mic' | 'blip-mic-busy' | 'blip-mic-on' | 'blip-mic-on-busy' | 'blip-mic-pulse' | 'blip-mic-pulse-busy'
  | 'blip-send' | 'blip-send-idle' | 'blip-send-still' | 'blip-send-idle-still';

const BLIP_SHEET_LOOKS = new Set<string>([
  'blip-head-icon', 'blip-head-new', 'blip-head-new-busy', 'blip-ctx-clear', 'blip-hands-free', 'blip-hands-free-on',
  'blip-mic', 'blip-mic-busy', 'blip-mic-on', 'blip-mic-on-busy', 'blip-mic-pulse', 'blip-mic-pulse-busy',
  'blip-send', 'blip-send-idle', 'blip-send-still', 'blip-send-idle-still',
]);
export const isWedgeBlipSheetLook = (look: string): look is WedgeBlipSheetLook => BLIP_SHEET_LOOKS.has(look);

/* Released Blip sheet (Wedge): the header buttons, the anchor chip clear button, the hands-free toggle, the mic and the send button. Each reads
   the exact legacy roles, and the declaration order is the order of the source. The caller owns the glyph, the label, the state attributes and the
   handler. No look draws a transparent target (the `blip-` hit rule removes it): each control sits in a row beside others at its released box.
   Head icon is the 30px round Teach button (a 1px card border, a transparent fill, the secondary ink). Head new is the same disc with the plus
   sign in 17px type; the busy one takes the default cursor. Ctx clear is the borderless 26px close mark at the end of the anchor chip, in the
   amber ink. Hands-free is a pill (30px tall at least, 5px by 11px padding, a 0.5px separator border, a transparent fill, the secondary ink, 11.5px
   bold type); on, it takes the amber border, the soft amber fill and the amber ink. Mic is a 44px disc with a 0.5px amber border and the amber ink;
   listening it fills with amber and prints the on-accent ink, and the pulse look adds the pulse animation of the source. Busy takes the default
   cursor. Send is a 44px disc: it can send when the box has text and no answer is pending, and then it takes the gold fill, the on-gold ink, the
   pointer and the fixed amber lift; it cannot, and then it takes the surface fill, the tertiary ink and the default cursor. The fade is the 160ms
   background and shadow transition of the source; the still looks have none, for reduced motion. */
const HEAD = 30, MIC = 44, HANDS_PAD = '5px 11px', HANDS_HEIGHT = 30, CLEAR_BOX = 26;
const EASE = 'var(--ease)';

function mic(on: boolean, pulse: boolean, busy: boolean): CSSProperties {
  const r = wedgeExactRole;
  return {
    width: MIC, height: MIC, borderRadius: radius.pill, border: `0.5px solid ${r('amber')}`, background: on ? r('amber') : 'transparent',
    color: on ? r('onAccent') : r('amber'), display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: busy ? 'default' : 'pointer',
    flex: '0 0 auto', WebkitTapHighlightColor: 'transparent', animation: pulse ? `prepPulse 1.4s ${EASE} infinite` : 'none',
  };
}

function send(canSend: boolean, still: boolean): CSSProperties {
  const r = wedgeExactRole;
  return {
    width: MIC, height: MIC, borderRadius: radius.pill, border: 'none', background: canSend ? r('gold') : r('surface'),
    color: canSend ? r('onGold') : r('labelTer'), fontSize: 18, fontWeight: weight.heavy, cursor: canSend ? 'pointer' : 'default', flex: '0 0 auto',
    boxShadow: canSend ? WEDGE_BLIP_SHEET.sendShadow : 'none',
    transition: still ? 'none' : `background 160ms ${EASE}, box-shadow 160ms ${EASE}`,
  };
}

export function wedgeBlipSheetStyle(look: WedgeBlipSheetLook): CSSProperties {
  const r = wedgeExactRole;
  if (look === 'blip-head-icon') return {
    border: `1px solid ${r('cardBorder')}`, background: 'transparent', color: r('labelSec'), borderRadius: radius.pill, width: HEAD, height: HEAD,
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
  };
  if (look === 'blip-head-new' || look === 'blip-head-new-busy') return {
    border: `1px solid ${r('cardBorder')}`, background: 'transparent', color: r('labelSec'), borderRadius: radius.pill, width: HEAD, height: HEAD,
    fontSize: 17, lineHeight: 1, cursor: look === 'blip-head-new-busy' ? 'default' : 'pointer', flex: '0 0 auto',
  };
  if (look === 'blip-ctx-clear') return {
    border: 'none', background: 'transparent', color: r('amber'), fontSize: 15, lineHeight: 1, cursor: 'pointer', padding: 2, flex: '0 0 auto',
    minWidth: CLEAR_BOX, minHeight: CLEAR_BOX, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  };
  if (look === 'blip-hands-free' || look === 'blip-hands-free-on') {
    const on = look === 'blip-hands-free-on';
    return {
      display: 'inline-flex', alignItems: 'center', gap: 6, padding: HANDS_PAD, minHeight: HANDS_HEIGHT, borderRadius: radius.pill,
      border: `0.5px solid ${on ? r('amber') : r('separator')}`, background: on ? r('amberSoft') : 'transparent', color: on ? r('amber') : r('labelSec'),
      fontSize: 11.5, fontWeight: weight.bold, cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
    };
  }
  if (look === 'blip-mic') return mic(false, false, false);
  if (look === 'blip-mic-busy') return mic(false, false, true);
  if (look === 'blip-mic-on') return mic(true, false, false);
  if (look === 'blip-mic-on-busy') return mic(true, false, true);
  if (look === 'blip-mic-pulse') return mic(true, true, false);
  if (look === 'blip-mic-pulse-busy') return mic(true, true, true);
  return send(look === 'blip-send' || look === 'blip-send-still', look === 'blip-send-still' || look === 'blip-send-idle-still');
}

/* The textarea of the box: 1px card border, surface fill, 16px type, a 96px cap and the sheet font. Off (the mic is listening) it dims to 60%. */
export type WedgeBlipInputVariant = 'wedge-blip-input' | 'wedge-blip-input-off';
export const isWedgeBlipInputVariant = (variant: unknown): variant is WedgeBlipInputVariant => variant === 'wedge-blip-input' || variant === 'wedge-blip-input-off';

export function wedgeBlipInputStyle(variant: WedgeBlipInputVariant): CSSProperties {
  const r = wedgeExactRole;
  return {
    flex: 1, resize: 'none', maxHeight: 96, padding: '10px 12px', borderRadius: 14, border: `1px solid ${r('cardBorder')}`, background: r('surface'),
    color: r('label'), fontSize: 16, fontFamily: r('font'), outline: 'none', opacity: variant === 'wedge-blip-input-off' ? 0.6 : 1,
  };
}
